import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import ProposalDetailsModal from '@/components/ProposalDetailsModal';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useVerifiedAction } from '@/components/verification/VerificationDialog';
import CommissionPreview from '@/components/CommissionPreview';
import { PropositionCardSkeleton } from '@/components/Skeletons';
import { ccySym } from '@/utils/listingParsers';
import '@/styles/propositions.css';
import '@/styles/skeletons.css';
import { cityFlag as flag } from '@/utils/cityFlag';
import {
  Lock, Zap, MessageCircle, Info, X, Check, Undo2, ArrowRight,
  Route, Quote, Clock, TimerOff, Inbox, Send, MailX, AlertCircle,
  Package, Navigation,
} from 'lucide-react';
import { BACKEND_URL } from '@/config';
import { fmtLeft } from '@/utils/timeLeft';

const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  PENDING:   { label: 'En attente',   tone: 'pending' },
  ACCEPTED:  { label: 'Acceptée',     tone: 'accepted' },
  REJECTED:  { label: 'Refusée',      tone: 'rejected' },
  WITHDRAWN: { label: 'Retirée',      tone: 'muted' },
  EXPIRED:   { label: 'Expirée',      tone: 'muted' },
};

const fmtPrice = (p) => `${Number(p.proposed_price ?? 0).toFixed(0)} ${ccySym(p.currency || 'EUR')}`;
const fmtConverted = (amount, currency) => {
  const n = Number(amount ?? 0);
  return `${n >= 10 ? n.toFixed(0) : n.toFixed(2)} ${ccySym(currency || 'EUR')}`;
};
const initials = (u) => `${(u?.first_name?.[0] || '?')}${(u?.last_name?.[0] || '')}`.toUpperCase();
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '·';

function PropositionCard({ p, tab, onAccept, onReject, onWithdraw, onOpenDetails, busyId }) {
  const navigate = useNavigate();
  const [awaitWithdraw, setAwaitWithdraw] = useState(false);
  const ship = p.shipment;
  const other = tab === 'received' ? p.from_user : p.to_user;

  const deadlineRaw = p.is_direct ? (p.response_deadline || p.expires_at) : p.expires_at;
  const expiresAt = deadlineRaw ? new Date(deadlineRaw) : null;
  const hoursLeft = expiresAt ? (expiresAt - new Date()) / 3_600_000 : null;
  const isUrgent  = hoursLeft !== null && hoursLeft < 24 && hoursLeft > 0;
  const direction = p.direction === 'CARRIER_TO_SENDER'
    ? (tab === 'received' ? 'Un transporteur veut prendre votre colis' : 'Vous proposez de transporter un colis')
    : (tab === 'received' ? 'Un expéditeur veut utiliser votre trajet' : 'Vous proposez votre colis pour ce trajet');

  const route = ship ? `${ship.departure_city} → ${ship.arrival_city}` : '·';
  const busy = busyId === p.id;

  // Basé sur la proposition uniquement — stable, indépendant de l'évolution de la mission
  const isSec = !!p.payment_reserved;
  // Proposition avec paiement séquestre dont le délai a expiré
  const isSecExpired = !!p.payment_reserved && p.status === 'EXPIRED';
  // Auto-rejet par expiration SLA (backend pose REJECTED + reject_reason "expiré")
  const isAutoExpired = p.status === 'REJECTED' && (p.reject_reason || '').toLowerCase().includes('expiré');
  const sLabel = isAutoExpired
    ? { label: 'Expirée', tone: 'muted' }
    : (STATUS_LABEL[p.status] || { label: p.status, tone: 'muted' });

  const typeBadge = isSec
    ? { label: 'Séquestre',   Icon: Lock,          cls: 'pr-type-sec'    }
    : p.is_direct
    ? { label: 'Directe',     Icon: Zap,           cls: 'pr-type-direct' }
    : { label: 'Négociation', Icon: MessageCircle, cls: 'pr-type-neg'    };

  const routeNote = isSec
    ? 'Paiement bloqué Stripe'
    : p.is_direct
    ? 'Prix fixe · Réponse sous 12h'
    : 'Négociation · 3 jours';

  const handleCardClick = (e) => {
    if (e.target.closest('button, a')) return;
    onOpenDetails(p);
  };

  return (
    <div
      className={`pr-card pr-status-${sLabel.tone}`}
      data-testid={`proposition-${p.id}`}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, a')) {
          e.preventDefault();
          onOpenDetails(p);
        }
      }}
    >
      <div className="pr-head">
        <div className="pr-av" aria-hidden="true">{initials(other)}</div>
        <div className="pr-head-text">
          <div className="pr-name">{other ? `${other.first_name || ''} ${other.last_name || ''}`.trim() : 'Membre'}</div>
          <div className="pr-sub">{direction}</div>
        </div>
        <div className="pr-head-meta">
          <span className={`pr-badge pr-badge-${sLabel.tone}`}>{sLabel.label}</span>
          <span className={`pr-type-badge ${typeBadge.cls}`}>
            <typeBadge.Icon size={12} strokeWidth={2} aria-hidden="true" />{typeBadge.label}
          </span>
        </div>
      </div>

      <div className="pr-route">
        <Route size={14} strokeWidth={2} aria-hidden="true" />
        {ship ? (
          <span className="pr-route-cities">
            {flag(ship.departure_city)}<strong>{ship.departure_city}</strong>
            <span className="pr-route-arr">→</span>
            {flag(ship.arrival_city)}<strong>{ship.arrival_city}</strong>
          </span>
        ) : '·'}
        {ship && <><span className="pr-route-sep">·</span><span className="pr-route-note">{routeNote}</span></>}
        {ship?.weight_kg ? <><span className="pr-route-sep">·</span><span className="pr-meta">{ship.weight_kg}kg</span></> : null}
      </div>

      {p.message && (
        <div className="pr-msg">
          <Quote size={13} strokeWidth={2} aria-hidden="true" />
          <span>{p.message}</span>
        </div>
      )}

      <div className="pr-foot">
        <div className="pr-price">
          <span className="pr-price-label">Prix proposé</span>
          <span className="pr-price-amount">{fmtPrice(p)}</span>
          {p.converted_amount != null && p.converted_currency && p.converted_currency !== p.currency && (
            <span className="pr-price-converted">
              ≈ {fmtConverted(p.converted_amount, p.converted_currency)}
            </span>
          )}
        </div>
        <div className="pr-time">{fmtDate(p.created_at)}</div>
      </div>

      {/* ── Niveau : Proposer (direct) — supprimé quand séquestre actif ── */}
      {!isSec && p.is_direct && p.status === 'PENDING' && tab === 'sent' && expiresAt && hoursLeft > 0 && (
        <div className="pr-level-chip pr-level-chip--deadline">
          <Zap size={13} strokeWidth={2} />
          <span>Expire le {fmtDate(deadlineRaw)} <strong>· encore {fmtLeft(hoursLeft)}</strong></span>
        </div>
      )}
      {!isSec && p.is_direct && p.status === 'PENDING' && tab === 'received' && expiresAt && hoursLeft > 0 && (
        <div className="pr-level-chip pr-level-chip--deadline">
          <Zap size={13} strokeWidth={2} />
          <span>Répondre avant {fmtDate(deadlineRaw)} <strong>· encore {fmtLeft(hoursLeft)}</strong></span>
        </div>
      )}
      {!isSec && p.is_direct && p.status === 'EXPIRED' && (
        <div className="pr-level-chip pr-level-chip--expired">
          <TimerOff size={13} strokeWidth={2} />
          <span>{p.reject_reason || 'Délai de réponse expiré (12h).'}</span>
        </div>
      )}

      {/* ── Niveau : Négocier — supprimé quand séquestre actif ── */}
      {!isSec && !p.is_direct && p.status === 'PENDING' && expiresAt && hoursLeft > 0 && (
        <div className="pr-level-chip pr-level-chip--negociation">
          <Clock size={13} strokeWidth={2} />
          <span>
            {tab === 'sent' ? 'Expire le' : 'À traiter avant le'} {fmtDate(deadlineRaw)} <strong>· encore {fmtLeft(hoursLeft)}</strong>
          </span>
        </div>
      )}
      {!isSec && !isSecExpired && !p.is_direct && p.status === 'EXPIRED' && (
        <div className="pr-level-chip pr-level-chip--expired">
          <TimerOff size={13} strokeWidth={2} />
          <span>{p.reject_reason || 'Délai de réponse expiré (72h).'}</span>
        </div>
      )}

      {/* ── Niveau : Séquestre ── */}
      {isSec && p.status === 'PENDING' && expiresAt && hoursLeft > 0 && (
        <div className="pr-level-chip pr-level-chip--deadline">
          <Lock size={13} strokeWidth={2} />
          <span>{tab === 'sent' ? 'Expire le' : 'Répondre avant'} {fmtDate(deadlineRaw)} <strong>· encore {fmtLeft(hoursLeft)}</strong></span>
        </div>
      )}
      {isSecExpired && (
        <div className="pr-level-chip pr-level-chip--expired">
          <TimerOff size={13} strokeWidth={2} />
          <span>{p.reject_reason || 'Délai de réponse expiré (12h).'}</span>
        </div>
      )}

      {p.status === 'PENDING' && tab === 'received' && (
        <div className="pr-actions">
          <button type="button" className="pr-btn pr-btn-ghost pr-btn-details" onClick={() => onOpenDetails(p)} data-testid={`btn-details-${p.id}`}>
            <Info size={14} strokeWidth={2} /> Détails
          </button>
          <button type="button" className="pr-btn pr-btn-ghost" disabled={busy} onClick={() => onReject(p)} data-testid={`btn-reject-${p.id}`}>
            <X size={14} strokeWidth={2.5} /> Refuser
          </button>
          <button type="button" className="pr-btn pr-btn-primary" disabled={busy} onClick={() => onAccept(p)} data-testid={`btn-accept-${p.id}`}>
            <Check size={14} strokeWidth={2.5} /> Accepter
          </button>
        </div>
      )}

      {p.status === 'PENDING' && tab === 'sent' && (
        <div className="pr-actions">
          <button type="button" className="pr-btn pr-btn-ghost pr-btn-details" onClick={() => onOpenDetails(p)} data-testid={`btn-details-${p.id}`}>
            <Info size={14} strokeWidth={2} /> Détails
          </button>
          {awaitWithdraw ? (
            <div className="ic-band" style={{ flex: 1 }}>
              <span className="ic-msg">Retirer <em>cette proposition ?</em></span>
              <div className="ic-actions">
                <button type="button" className="ic-yes ic-yes--red" onClick={() => { setAwaitWithdraw(false); onWithdraw(p); }} data-testid={`btn-withdraw-confirm-${p.id}`}>Oui</button>
                <button type="button" className="ic-no" onClick={() => setAwaitWithdraw(false)}>Non</button>
              </div>
            </div>
          ) : (
            <button type="button" className="pr-btn pr-btn-ghost" disabled={busy} onClick={() => setAwaitWithdraw(true)} data-testid={`btn-withdraw-${p.id}`}>
              <Undo2 size={14} strokeWidth={2} /> Retirer
            </button>
          )}
        </div>
      )}

      {p.status === 'ACCEPTED' && p.mission_id && (
        <div className="pr-actions">
          <button type="button" className="pr-btn pr-btn-ghost pr-btn-details" onClick={() => onOpenDetails(p)} data-testid={`btn-details-${p.id}`}>
            <Info size={14} strokeWidth={2} /> Détails
          </button>
          <button type="button" className="pr-btn pr-btn-mission" onClick={() => navigate(`/app/missions/${p.mission_id}`)} data-testid={`btn-go-mission-${p.id}`}>
            <ArrowRight size={14} strokeWidth={2} /> Voir la mission
          </button>
        </div>
      )}

      {p.status === 'REJECTED' && p.reject_reason && (
        <>
          {isAutoExpired && (
            <div className="pr-level-chip pr-level-chip--direct" style={{ marginBottom: 6 }}>
              <Zap size={13} strokeWidth={2} />
              <span>Proposition directe</span>
            </div>
          )}
          <div className="pr-reject-reason">
            <AlertCircle size={14} strokeWidth={2} />
            <div>
              <div className="pr-reject-reason-label">Motif du refus</div>
              <div className="pr-reject-reason-text">{p.reject_reason}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Sélecteur de rôle pour utilisateurs hybrides
function RoleSelector({ roleFilter, onChange, senderCount, carrierCount }) {
  const opts = [
    {
      key: 'as_sender',
      Icon: Package,
      label: 'Expéditeur',
      color: '#0e7a68',
      bg: '#ecfdf5',
      count: senderCount,
    },
    {
      key: 'as_carrier',
      Icon: Navigation,
      label: 'Transporteur',
      color: '#C8832A',
      bg: '#fef9f0',
      count: carrierCount,
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
      {opts.map(o => {
        const active = roleFilter === o.key;
        return (
          <button
            key={o.key}
            type="button"
            data-testid={o.key === 'as_sender' ? 'role-btn-sender' : 'role-btn-carrier'}
            onClick={() => onChange(active ? 'all' : o.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 18px', borderRadius: 24, fontSize: 13.5, fontWeight: 600,
              border: `2px solid ${active ? o.color : '#E5E7EB'}`,
              background: active ? o.bg : '#fff',
              color: active ? o.color : '#6B7280',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <o.Icon size={15} strokeWidth={2} />
            {o.label}
            {o.count > 0 && (
              <span style={{
                background: active ? o.color : '#E5E7EB',
                color: active ? '#fff' : '#374151',
                borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700,
              }}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Calcule le rôle d'une proposition selon le tab et la direction
function getPropositionRole(p, tab) {
  const dir = p.direction;
  if (tab === 'received') {
    // received = user is to_user_id
    // SENDER_TO_CARRIER → sender proposes to use MY trip → I'm carrier
    // CARRIER_TO_SENDER → carrier proposes to carry MY colis → I'm sender
    return dir === 'SENDER_TO_CARRIER' ? 'as_carrier' : 'as_sender';
  } else {
    // sent = user is from_user_id
    // CARRIER_TO_SENDER → I propose to carry their colis → I'm carrier
    // SENDER_TO_CARRIER → I propose to use their trip → I'm sender
    return dir === 'CARRIER_TO_SENDER' ? 'as_carrier' : 'as_sender';
  }
}

export default function PropositionsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'sent' ? 'sent' : 'received';
  const [tab, setTab] = useState(initialTab);
  const [typeFilter, setTypeFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [detailsProp, setDetailsProp] = useState(null);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const isSender  = user?.roles?.includes('ROLE_SENDER');
  const isCarrier = user?.roles?.includes('ROLE_CARRIER');
  const isHybrid  = isSender && isCarrier;

  // Pas de filtre rôle par défaut — l'utilisateur hybride voit tout, et peut filtrer via le sélecteur
  const [roleFilter, setRoleFilter] = useState('all');

  // Numéro du dernier chargement : la réponse d'un onglet quitté entre-temps ne doit pas écraser la liste
  const lastLoadRef = useRef(0);
  const load = useCallback(async () => {
    const loadId = ++lastLoadRef.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/propositions/${tab}`, { params: { limit: 50 } });
      if (loadId !== lastLoadRef.current) return;
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setItems(data);
      if (tab === 'received') setPendingCount(res.data?.meta?.pending || 0);
    } catch {
      if (loadId === lastLoadRef.current) toast.error('Impossible de charger les propositions');
    } finally {
      if (loadId === lastLoadRef.current) setLoading(false);
    }
  }, [tab, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!detailsProp) return;
    const fresh = items.find((it) => it.id === detailsProp.id);
    if (fresh) setDetailsProp(fresh);
    else setDetailsProp(null);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtrage par rôle pour les hybrides + filtre type + tri : Séquestre → Directe → Négociation, puis date desc
  const filteredItems = useMemo(() => {
    let base = (!isHybrid || roleFilter === 'all') ? items : items.filter(p => getPropositionRole(p, tab) === roleFilter);
    if (typeFilter === 'sec')    base = base.filter(p => p.payment_reserved);
    else if (typeFilter === 'direct') base = base.filter(p => !p.payment_reserved && p.is_direct);
    else if (typeFilter === 'neg')    base = base.filter(p => !p.payment_reserved && !p.is_direct);
    const typePriority = (p) => p.payment_reserved ? 0 : p.is_direct ? 1 : 2;
    return [...base].sort((a, b) => {
      if (typeFilter === 'all') return new Date(b.created_at) - new Date(a.created_at);
      const dt = typePriority(a) - typePriority(b);
      if (dt !== 0) return dt;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [items, isHybrid, roleFilter, tab, typeFilter]);

  // Compteurs pour les chips de filtre (sur la base rôle, avant filtre type)
  const typeCounts = useMemo(() => {
    const base = (!isHybrid || roleFilter === 'all') ? items : items.filter(p => getPropositionRole(p, tab) === roleFilter);
    return {
      sec:    base.filter(p => p.payment_reserved).length,
      direct: base.filter(p => !p.payment_reserved && p.is_direct).length,
      neg:    base.filter(p => !p.payment_reserved && !p.is_direct).length,
    };
  }, [items, isHybrid, roleFilter, tab]);

  // Comptages par rôle pour le sélecteur
  const senderCount = useMemo(() => items.filter(p => getPropositionRole(p, tab) === 'as_sender').length, [items, tab]);
  const carrierCount = useMemo(() => items.filter(p => getPropositionRole(p, tab) === 'as_carrier').length, [items, tab]);

  // Pending count — pour hybrides, compte selon le filtre actif (ou tout si 'all')
  const pendingFiltered = useMemo(() => {
    if (!isHybrid) return pendingCount;
    if (roleFilter === 'all') return items.filter(p => p.status === 'PENDING').length;
    return items.filter(p => p.status === 'PENDING' && getPropositionRole(p, 'received') === roleFilter).length;
  }, [items, isHybrid, roleFilter, pendingCount]);

  // Accepter crée une mission (create_mission) : sans les vérifications, la fenêtre explique au lieu d'un refus du serveur
  const acceptGuard = useVerifiedAction(user, 'create_mission');
  const onAccept = (p) => acceptGuard.run(() => setAcceptTarget(p));

  const doAccept = async () => {
    const p = acceptTarget;
    setAcceptTarget(null);
    setBusyId(p.id);
    try {
      const res = await axios.post(`${API}/propositions/${p.id}/accept`);
      toast.success('Proposition acceptée — mission créée');
      await load();
      if (res.data?.mission_id) {
        setTimeout(() => { window.location.href = `/app/missions/${res.data.mission_id}`; }, 600);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onReject = (p) => { setRejectTarget(p); setRejectReason(''); };

  const doReject = async () => {
    const p = rejectTarget;
    setRejectTarget(null);
    setBusyId(p.id);
    try {
      await axios.post(`${API}/propositions/${p.id}/reject`, { reason: rejectReason });
      toast.info('Proposition refusée');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onWithdraw = async (p) => {
    setBusyId(p.id);
    try {
      await axios.post(`${API}/propositions/${p.id}/withdraw`);
      toast.info('Proposition retirée');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const emptyLabel = tab === 'received' ? 'reçue' : 'envoyée';
  const emptyHint = tab === 'received'
    ? 'Quand un membre vous proposera un transport ou un colis, il apparaîtra ici.'
    : 'Parcourez la marketplace et envoyez votre première proposition.';

  return (
    <div className="propositions-page" data-testid="propositions-page">
      <div className="pr-header">
        <div>
          <h1 className="pr-title">Propositions</h1>
          <p className="pr-subtitle">Échanges initiés avant qu'une mission ne soit officiellement validée.</p>
        </div>
      </div>

      {/* Sélecteur de rôle (hybrides uniquement) */}
      {isHybrid && (
        <RoleSelector
          roleFilter={roleFilter}
          onChange={setRoleFilter}
          senderCount={senderCount}
          carrierCount={carrierCount}
        />
      )}

      {/* Onglets Reçues / Envoyées */}
      <div className="pr-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'received'}
          className={`pr-tab${tab === 'received' ? ' on' : ''}`}
          onClick={() => { setTab('received'); setTypeFilter('all'); }}
          data-testid="tab-received"
        >
          <Inbox size={15} strokeWidth={2} /> Reçues
          {(isHybrid ? pendingFiltered : pendingCount) > 0 && (
            <span className="pr-tab-badge">{isHybrid ? pendingFiltered : pendingCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'sent'}
          className={`pr-tab${tab === 'sent' ? ' on' : ''}`}
          onClick={() => { setTab('sent'); setTypeFilter('all'); }}
          data-testid="tab-sent"
        >
          <Send size={15} strokeWidth={2} /> Envoyées
        </button>
      </div>

      {/* Filtre par type de proposition */}
      {(typeCounts.sec + typeCounts.direct + typeCounts.neg) > 0 && (
        <div className="pr-type-filters">
          <button type="button" className={`pr-type-filter-chip${typeFilter === 'all' ? ' on' : ''}`} onClick={() => setTypeFilter('all')}>
            Tous
          </button>
          {typeCounts.sec > 0 && (
            <button type="button" className={`pr-type-filter-chip pr-tfc-sec${typeFilter === 'sec' ? ' on' : ''}`} onClick={() => setTypeFilter('sec')}>
              <Lock size={13} strokeWidth={2} aria-hidden="true" /> Séquestre <span className="pr-tfc-count">{typeCounts.sec}</span>
            </button>
          )}
          {typeCounts.direct > 0 && (
            <button type="button" className={`pr-type-filter-chip pr-tfc-direct${typeFilter === 'direct' ? ' on' : ''}`} onClick={() => setTypeFilter('direct')}>
              <Zap size={13} strokeWidth={2} aria-hidden="true" /> Directe <span className="pr-tfc-count">{typeCounts.direct}</span>
            </button>
          )}
          {typeCounts.neg > 0 && (
            <button type="button" className={`pr-type-filter-chip pr-tfc-neg${typeFilter === 'neg' ? ' on' : ''}`} onClick={() => setTypeFilter('neg')}>
              <MessageCircle size={13} strokeWidth={2} aria-hidden="true" /> Négociation <span className="pr-tfc-count">{typeCounts.neg}</span>
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="pr-grid" data-testid="propositions-skeleton">
          {Array.from({ length: 4 }).map((_, i) => (
            <PropositionCardSkeleton key={i} />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="pr-empty">
          <MailX size={48} strokeWidth={1.5} />
          <h3>
            Aucune proposition {emptyLabel}
            {isHybrid && roleFilter !== 'all' && ` en tant que ${roleFilter === 'as_sender' ? 'Expéditeur' : 'Transporteur'}`}
          </h3>
          <p>{emptyHint}</p>
        </div>
      ) : (
        <div className="pr-grid">
          {filteredItems.map((p) => (
            <PropositionCard
              key={p.id}
              p={p}
              tab={tab}
              busyId={busyId}
              onAccept={onAccept}
              onReject={onReject}
              onWithdraw={onWithdraw}
              onOpenDetails={setDetailsProp}
            />
          ))}
        </div>
      )}

      <ProposalDetailsModal
        proposition={detailsProp}
        tab={tab}
        busy={busyId === detailsProp?.id}
        onClose={() => setDetailsProp(null)}
        onAccept={onAccept}
        onReject={onReject}
        onWithdraw={onWithdraw}
      />

      {acceptGuard.dialog}

      <ConfirmDialog
        open={!!acceptTarget}
        onClose={() => setAcceptTarget(null)}
        onConfirm={doAccept}
        variant="financial"
        icon="check"
        title="Accepter la proposition"
        subtitle={acceptTarget ? `${acceptTarget.from_user?.first_name || 'Ce membre'} ${acceptTarget.from_user?.last_name || ''}`.trim() : undefined}
        amount={acceptTarget ? fmtPrice(acceptTarget) : undefined}
        details={acceptTarget && getPropositionRole(acceptTarget, 'received') === 'as_carrier' && acceptTarget.proposed_price
          ? <CommissionPreview amount={acceptTarget.proposed_price} currency={acceptTarget.currency || 'EUR'} paymentReserved={!!acceptTarget.payment_reserved} totalLabel="Total à recevoir" />
          : undefined}
        confirmLabel="Confirmer l'acceptation"
        busy={!!busyId}
      />

      <ConfirmDialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={doReject}
        variant="destructive"
        icon="ban"
        title="Refuser la proposition ?"
        subtitle={rejectTarget ? `De ${rejectTarget.from_user?.first_name || 'ce membre'} ${rejectTarget.from_user?.last_name || ''}`.trim() : undefined}
        confirmLabel="Confirmer le refus"
        inputPrompt={{ label: 'Motif du refus (optionnel)', placeholder: 'Ex: prix trop élevé, dates incompatibles…' }}
        inputValue={rejectReason}
        onInputChange={setRejectReason}
        busy={!!busyId}
      />
    </div>
  );
}
